import { Component, Prop} from 'vue-property-decorator';
import TombolaBaseComponent from '@/components/base/base.component';
import { ChatContextInteractionType, ChatContextInteractionSource } from '../chat/_domain/modules/chat-context-menu';
import { ChatContextInteractionSubmit } from './models';
import { IEventBusService } from '@/app-modules/event-bus/event-bus-service';
import { IGameConfigurationService, IChatRoomGameViewModel } from '@/app-modules/config';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';

interface IOptions {
  value: number | string;
  text: string;
  disabled?: boolean;
  selected?: boolean;
}

enum ChatRoomDropDownAccess{
  DisableDropDownBoxes = 0,
  DisplayDropDownBoxes = 1,
  ControlDropDownBoxes = 2
}

@Component({
  name: 'ChatContextInteractionComponent'
})
export default class ChatContextInteractionComponent extends TombolaBaseComponent {
  @Prop() public callbackFunction: (chatContextAction: ChatContextInteractionType, actionReason?: string, actionChatRoom?: string) => void;
  @Prop() public chatContextInteraction: string;
  @Prop() public playerUsername: string;
  @Prop() public chatRoomName: string;
  @Prop() public modalGuid: string;
  @Prop({ type: Number }) private chatContextInteractionSource: ChatContextInteractionSource;
  private confirmedChatRoomName: string = '';
  private chatContextInteractionReason: string = '';
  private chatRoomList: IChatRoomGameViewModel[];
  private chatRoomDropDownAccess: ChatRoomDropDownAccess = ChatRoomDropDownAccess.DisableDropDownBoxes;
  private chatRoomDropDownGames: IOptions[] = [];
  private chatRoomDropDownRooms: IOptions[] = [];
  private chatRoomSelectedGame: string = '';
  private chatRoomSelectedRoom: number = 0;
  private messageGameNotFoundError: boolean = false;

  constructor(){
    super();
    this.confirmedChatRoomName = this.cleanChatRoomName();

    if ([ChatContextInteractionType.BanPlayer, ChatContextInteractionType.SinPlayer].includes(this.chatContextInteraction as ChatContextInteractionType)) {
      this.chatRoomDropDownAccess = ChatRoomDropDownAccess.DisplayDropDownBoxes;
      if (!this.confirmedChatRoomName ||
          this.confirmedChatRoomName.length === 0 ||
          !this.chatContextInteractionSource ||
          this.chatContextInteractionSource === ChatContextInteractionSource.SearchResultItem)
      {
            this.chatRoomDropDownAccess = ChatRoomDropDownAccess.ControlDropDownBoxes;
      }
    }

    this.setupChatRooms();
  }

  private cleanChatRoomName(): string {
    return (this.chatRoomName && this.chatRoomName.toLowerCase() !== 'unknown') ? this.chatRoomName : '';
  }

  private async loadChatRooms() {
    const gameService: IGameConfigurationService = this.$container.get(SERVICE_IDENTIFIERS.IGameConfigurationService);
    const chatRooms: IChatRoomGameViewModel[] = await gameService.getAllChatooms();

    this.chatRoomList = chatRooms;
  }

  private async setupChatRooms(){
    await this.loadChatRooms();
    this.chatRoomDropDownGames = this.getGameOptions([... new Set(this.chatRoomList.map((x) => x.GameName))]);
    this.loadCurrentGameIntoDropDowns();
  }

  private get eventBusService(): IEventBusService {
    return this.$container.get(SERVICE_IDENTIFIERS.IEventBusService);
  }

  private loadCurrentGameIntoDropDowns(){
    const currentGameAndRoomResults = this.chatRoomList.filter((x) => this.roomNamesMatch(x.RoomName, this.confirmedChatRoomName));
    if (currentGameAndRoomResults.length > 0) {
      const currentGameAndRoom = currentGameAndRoomResults[0];

      this.chatRoomSelectedGame = currentGameAndRoom.GameName;
      this.populateGameRooms(currentGameAndRoom.GameName);
      this.chatRoomSelectedRoom = currentGameAndRoom.Id;
    } else {
      this.confirmedChatRoomName = '';
      this.chatRoomSelectedGame = '';
      this.chatRoomSelectedRoom = 0;
      if (this.chatRoomDropDownAccess === ChatRoomDropDownAccess.DisplayDropDownBoxes) {
        this.chatRoomDropDownAccess = ChatRoomDropDownAccess.ControlDropDownBoxes;
      }
    }
  }

  private populateGameRooms(chosenGame: string) {
    this.chatRoomDropDownRooms = [... new Set(this.chatRoomList.filter((x) => x.GameName === chosenGame).map((x) => this.getRoomOptions(x)))];
  }

  private handleGameChange(chosenGame: string) {
    this.messageGameNotFoundError = false;
    this.confirmedChatRoomName = '';
    this.populateGameRooms(chosenGame);
    this.chatRoomSelectedRoom = 0;
  }

  private handleRoomChange(chosenRoom: number) {
    try {
      this.confirmedChatRoomName = this.chatRoomList.filter((x) => x.Id === chosenRoom)[0].RoomName;
    } catch {
      this.confirmedChatRoomName = '';
    }
    this.messageGameNotFoundError = (this.confirmedChatRoomName === '');
  }

  private handleClearChatRoomName() {
    this.confirmedChatRoomName = '';
    this.loadCurrentGameIntoDropDowns();
  }

  public getGameOptions(entries: string[]): IOptions[]{
    const options: IOptions[] = [];
    for (const entry of entries) {
      options.push({value: entry, text: entry});
    }

    return options.sort((roomA, roomB) => {
      if (roomA.text.toUpperCase() < roomB.text.toUpperCase()) {
        return -1;
      }
      if (roomA.text.toUpperCase() > roomB.text.toUpperCase()) {
        return 1;
      }

      return 0;
    });
  }

  public getRoomOptions(entry: IChatRoomGameViewModel): IOptions{
    let roomEntryText = entry.RoomName;
    if (
        entry.ClubName &&
        ![entry.RoomName, entry.RoomName + ' Room'].includes(entry.ClubName) &&
        ![entry.GameName, entry.GameName + ' Room'].includes(entry.ClubName)
        ) {
      roomEntryText += ' (' + entry.ClubName + ')';
    }
    return {value: entry.Id, text: roomEntryText};
  }

  private handleChatContextInteractionConfirmation() {
    if (this.callbackFunction) {
      this.eventBusService.getEventBus().$emit(ChatContextInteractionSubmit, this.modalGuid);
      this.callbackFunction(this.chatContextInteraction as ChatContextInteractionType, this.chatContextInteractionReason, this.confirmedChatRoomName);
    }
  }

  private roomNamesMatch(roomA: string, roomB: string): boolean {
    const pattern = /[^A-Za-z0-9\-]|room/g;
    return roomA.toLocaleLowerCase().replace(pattern, '') === roomB.toLocaleLowerCase().replace(pattern, '');
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/chat-context-interaction/chat-context-interaction.component.ts